/**
 * Phase 328 wave 5 — the LOOKUP MODEL: how a schema-declared lookup becomes a request, and how
 * the connector envelope it returns becomes field values.
 *
 * ── Why the mapping lives on the client ───────────────────────────────────────────────────
 * The endpoint returns the connector envelope UNMAPPED and refuses to interpret it, so the
 * `populate` / `populate_group` grammar is evaluated exactly once, here — where the answers
 * live. That is what keeps the whole feature declarative: this module knows how to read a
 * DECLARATION, and knows nothing about any connector, tenant, workflow or form. Swapping the
 * integration behind a lookup changes a YAML line and no code.
 *
 * ── The declaration is the whitelist, in both directions ──────────────────────────────────
 *   request:         only `${fields.<name>}` params are sent; a declared LITERAL is taken from
 *                    the declaration server-side and never from the client.
 *   populate:        only fields the schema declares, and only scalars — a group belongs in
 *                    `populate_group` (ARD-DSL-038).
 *   populate_group:  only subfields the target group declares; bounded by the group's own
 *                    `min` / `max` so autofill can never produce a shape the subject could not
 *                    have typed. Phase 523: one or more SOURCES, each with its own row filter,
 *                    collapsed on a declared `merge_key` and MERGED into the answers the subject
 *                    already gave — see {@link groupRows}.
 * Anything a path does not resolve to is simply NOT written — a partial envelope autofills
 * what it can and leaves the rest to the subject (AC-4's manual-completion floor).
 *
 * ── Autofill, not lock ────────────────────────────────────────────────────────────────────
 * Every function here returns VALUES. Nothing marks a field populated, read-only or verified:
 * a populated field is an ordinary answer the subject can overwrite, which is the whole
 * behavioural difference between "we filled this in for you" and "we decided this for you".
 *
 * And it never OVERWRITES one either: {@link lookupPatch} takes the subject's current answers, so
 * a second run of the same lookup keeps every row they added and every cell they typed.
 */
import type {
  FieldSpec,
  FieldValue,
  FormLookup,
  FormSchema,
  FormValues,
  GroupFieldSpec,
  GroupRow,
  LookupPopulateGroup,
  LookupPopulateGroupSource,
  ScalarFieldSpec,
} from "../types.js";
import type { LookupResult } from "../api/formApi.js";
import {
  asRows,
  asStringArray,
  blankRow,
  declaredFields,
  fromRowCell,
  isBlankCell,
  isGroupField,
  selectPrefill,
  toRowCell,
  toRowCellFor,
} from "./formModel.js";
import { addressFromEnvelope, envelopeDisplayValue, isAddressEmpty } from "./formDesign.js";
import { isTruthy, isVisible } from "./predicates.js";
import { ITEM_PREFIX, readPath } from "./paths.js";

/**
 * A lookup `request` param that substitutes a form field — the TS half of the one grammar
 * declared in `packages/workflow-schema/src/schema/form-grammar.ts`
 * (FORM_LOOKUP_REQUEST_FIELD_TEMPLATE) and enforced at deploy time by ARD-DSL-038. Anything
 * else in the declaration is a literal constant the server supplies.
 */
const REQUEST_FIELD_TEMPLATE = /^\$\{fields\.([a-zA-Z_][a-zA-Z0-9_]*)\}$/;

/**
 * The `item.` root and the dotted reader that walks it both live in `./paths.js` — the mapping
 * half here ({@link itemValue}) and the ROW-FILTER half in `predicates.ts` address one envelope
 * row with one grammar, so they read it through one function rather than two that drift.
 * {@link readPath} is re-exported because this module's own API has always carried it.
 */
export { readPath };

// ── Subject-facing copy ────────────────────────────────────────────────────────────────────
//
// Card-pinned, and mirrored by the server's own constants (FormsApi LookupMessageNotFound /
// LookupMessageUnavailable) — the server sends the copy, these are what the subject reads when
// it could not (a 429, a dead network, a body that never arrived).
//
// Card #2649 — the not-found sentence fits the search that ran. Every lookup that reaches
// {@link lookupMessage} is a DECLARED lookup, and every declared lookup is a company search by
// name or by number in one box, so its floor names both. The postcode search never comes through
// here: `AddressInput` owns its own not-found sentence (ADDRESS_LOOKUP_NOT_FOUND), as the server
// does on its reserved address arm (FormsApi LookupMessageAddressNotFound).

/**
 * Phase 449 — this line no longer says the filled fields are "below" it.
 *
 * The status renders next to the button that ran the lookup, but a declaration's `populate` /
 * `populate_group` targets are ordinary field names and nothing constrains them to the step the
 * lookup was triggered from: a real client declaration fills two fields on the NEXT step and a
 * repeating group three steps after that. "Below" was therefore false for exactly the case that
 * produced this card — the subject read a confirmation, looked down, and saw nothing that had
 * changed. WHERE the answers landed is now the `AutofillNotice`'s job, rendered on the step that
 * actually holds them; this line's job is only to say that the lookup worked and that whatever
 * it wrote is the subject's to correct.
 */
export const LOOKUP_MESSAGE_SUCCESS =
  "We found the details and filled them in for you. Check them and change anything that is wrong.";
export const LOOKUP_MESSAGE_NOT_FOUND = "No company found. Check the name or number and try again.";
export const LOOKUP_MESSAGE_UNAVAILABLE = "Enter your details manually.";
export const LOOKUP_MESSAGE_RATE_LIMITED = "Too many lookups just now. Please wait a moment and try again.";

/**
 * What the subject reads after a lookup. The server's message wins when it sent one (it is
 * the same card-pinned copy, decided at the layer that knows what actually happened); the
 * constants above are the floor for the arms that carry no body.
 *
 * Every degraded arm is a NOTICE, never an error: it tells the subject what to do next, and
 * nothing about it blocks or disables the form.
 */
export function lookupMessage(result: LookupResult): string {
  if (result.ok) return LOOKUP_MESSAGE_SUCCESS;

  const supplied = typeof result.message === "string" ? result.message.trim() : "";
  if (supplied.length > 0) return supplied;

  switch (result.outcome) {
    case "not_found":
      return LOOKUP_MESSAGE_NOT_FOUND;
    case "rate_limited":
      return LOOKUP_MESSAGE_RATE_LIMITED;
    default:
      return LOOKUP_MESSAGE_UNAVAILABLE;
  }
}

// ── Request ────────────────────────────────────────────────────────────────────────────────

/** Every lookup the schema declares, anywhere (lookups are declared on the step they trigger from). */
export function declaredLookups(schema: FormSchema): FormLookup[] {
  return (schema.steps ?? []).flatMap((step) => step.lookups ?? []);
}

/**
 * The `args` a lookup posts: the current answer for each `${fields.<name>}` request param,
 * projected to its wire string and trimmed (a company number pasted with a trailing space is
 * the subject's typo to forgive, not the connector's to fail on).
 *
 * Declared LITERALS are deliberately absent: the server takes them from the declaration, so
 * sending them would be noise at best and a widening attempt at worst.
 */
export function lookupArgs(lookup: FormLookup, values: FormValues): Record<string, string> {
  const args: Record<string, string> = {};
  for (const [param, declared] of Object.entries(lookup.request ?? {})) {
    const name = fieldReference(declared);
    if (name === undefined) continue;
    args[param] = toRowCell(values[name]).trim();
  }
  return args;
}

/**
 * Is there anything to look up yet? True once every field the request references has a value
 * — which is also why the endpoint's `missing_arg` 400 is unreachable from this UI.
 * A declaration whose params are all literals is always ready.
 */
export function lookupReady(lookup: FormLookup, values: FormValues): boolean {
  return Object.values(lookupArgs(lookup, values)).every((value) => value.length > 0);
}

function fieldReference(declared: unknown): string | undefined {
  if (typeof declared !== "string") return undefined;
  return REQUEST_FIELD_TEMPLATE.exec(declared)?.[1];
}

// ── Response → values ──────────────────────────────────────────────────────────────────────

/**
 * The answers a successful lookup writes, as a PATCH over the current values — never a whole
 * replacement, so nothing the declaration does not name is touched.
 *
 * `payload` is the lookup RESULT, which is what makes the declared paths read literally: the
 * envelope sits under `response`, so `response.company_name` in the YAML is `response.company_name`
 * here. An empty patch means "nothing was mappable" and the caller degrades accordingly.
 */
export function lookupPatch(
  schema: FormSchema,
  lookup: FormLookup,
  payload: unknown,
  current: FormValues = {},
): FormValues {
  const index = fieldIndex(schema);
  const patch: FormValues = scalarPatch(index, lookup.populate, (path) => readPath(payload, path));

  // NOTHING to contribute leaves the subject's own rows alone — the group key is absent from
  // the patch entirely, so the caller's spread cannot touch the answer.
  const contribution = groupContribution(index, lookup, payload, current);
  if (contribution !== undefined) patch[contribution.target] = contribution.rows;

  return patch;
}

/**
 * WHICH rows of a group autofill the upstream contributed — the provenance behind the "From the
 * register" chip — together with the group the declaration targets.
 *
 * It exists so a caller never has to re-spell the `populate_group` shape (read `target`, look at
 * `patch[target]`, guess which of its rows are new): the target and the row indices come back from
 * ONE call, computed by the SAME merge that produced the patch. It takes the same `current`
 * answers {@link lookupPatch} takes, because provenance is a function of what was already there —
 * a row the subject typed is theirs even when the register happens to carry the same human, and a
 * row whose blanks were filled from the register is still the subject's row.
 *
 * `undefined` means "this lookup contributed no group rows at all" — no `populate_group`, an
 * undeclared or non-group target, or nothing that survived the declaration's filters. An empty
 * `rows` means the register contributed only to rows the subject already had.
 */
export function lookupGroupProvenance(
  schema: FormSchema,
  lookup: FormLookup,
  payload: unknown,
  current: FormValues = {},
): LookupGroupProvenance | undefined {
  const contribution = groupContribution(fieldIndex(schema), lookup, payload, current);
  if (contribution === undefined) return undefined;
  return { target: contribution.target, rows: contribution.contributed };
}

/** The group a lookup fills, and the rows of it the upstream — not the subject — put there. */
export interface LookupGroupProvenance {
  /** The declared group field the rows belong to. */
  readonly target: string;
  /** Indices into the patched rows that the upstream contributed, ascending. */
  readonly rows: readonly number[];
}

/** The merged rows for a lookup's declared group target, with their provenance. */
interface GroupContribution {
  readonly target: string;
  readonly rows: GroupRow[];
  readonly contributed: number[];
}

/**
 * THE group half, resolved once: the declaration's target checked against the schema, and the
 * merge run against the subject's current rows. {@link lookupPatch} takes the rows from it and
 * {@link lookupGroupProvenance} takes the indices, so the two can never disagree about which row
 * came from where.
 */
function groupContribution(
  index: Map<string, FieldSpec>,
  lookup: FormLookup,
  payload: unknown,
  current: FormValues,
): GroupContribution | undefined {
  const group = lookup.populate_group;
  if (group === undefined) return undefined;
  const spec = index.get(group.target);
  if (spec === undefined || !isGroupField(spec)) return undefined;
  const merged = groupRows(spec, group, payload, current[group.target]);
  if (merged === undefined) return undefined;
  return { target: group.target, rows: merged.rows, contributed: merged.contributed };
}

function fieldIndex(schema: FormSchema): Map<string, FieldSpec> {
  return new Map(declaredFields(schema).map((field) => [field.name, field]));
}

/**
 * THE scalar half of the mapping grammar, once: `{ <declared field>: <declared path> }` evaluated
 * against whatever root the caller resolves paths in — the whole envelope for `lookup.populate`,
 * ONE candidate item for `choose.populate`. Both write through the same {@link scalarValue}, so a
 * pick fills a field exactly as an autofill does and there is no second grammar to keep in step.
 */
function scalarPatch(
  index: Map<string, FieldSpec>,
  mapping: Readonly<Record<string, string>> | undefined,
  resolve: (declared: string) => unknown,
): FormValues {
  const patch: FormValues = {};
  for (const [name, declared] of Object.entries(mapping ?? {})) {
    const spec = index.get(name);
    // Undeclared, or a group target in the scalar half — both are ARD-DSL-038 violations that
    // cannot pass deploy validation; skipped rather than trusted, because a hand-edited
    // declaration is still a declaration.
    if (spec === undefined || isGroupField(spec)) continue;
    const value = scalarValue(spec, resolve(declared));
    if (value !== undefined) patch[name] = value;
  }
  return patch;
}

/**
 * One `item.`-rooted declaration read against ONE array item — or, when it is not `item.`-rooted,
 * the literal the declaration wrote (the party type every row from an array carries). Shared by
 * {@link groupRows} and {@link chooseCandidates} / {@link choosePatch}.
 */
function itemValue(item: unknown, declared: unknown): unknown {
  if (typeof declared !== "string") return undefined;
  return declared.startsWith(ITEM_PREFIX) ? readPath(item, declared.slice(ITEM_PREFIX.length)) : declared;
}

/**
 * One resolved envelope value as the target field's own answer type. A value with no reading
 * for that type (an object into a text box, an empty string, an absent path) returns
 * `undefined` — "leave this field alone".
 */
function scalarValue(spec: ScalarFieldSpec, resolved: unknown): FieldValue | undefined {
  if (resolved === undefined || resolved === null) return undefined;

  if (spec.type === "boolean") return isTruthy(resolved);

  // An address block takes the envelope's address OBJECT (or a one-line string) — the
  // subject sees it split into lines they can correct, not a blob in a text box.
  if (spec.type === "address") {
    const address = addressFromEnvelope(resolved);
    return isAddressEmpty(address) ? undefined : address;
  }

  if (spec.type === "checkbox_group") {
    const selections = (Array.isArray(resolved) ? resolved : [resolved])
      .map(toRowCell)
      .filter((item) => item.length > 0);
    return selections.length > 0 ? selections : undefined;
  }

  // Card #2908 — a `select` takes only a value its dropdown can show ({@link selectPrefill}).
  const text = selectPrefill(spec, toRowCell(resolved));
  return text.length > 0 ? text : undefined;
}

/**
 * Phase 523 — THE GROUP HALF, in three stages that each do one thing:
 *
 *   1. BUILD    one candidate row per envelope item, per source, in DECLARATION ORDER, with the
 *               source's `where` filter applied BEFORE the mapping (an item that is not ours
 *               contributes nothing, rather than a row we then have to recognise and drop);
 *   2. COLLAPSE candidates that share the normalised `merge_key` cell into ONE — the same human
 *               on two registers is one row, with a list-valued subfield UNIONING first-seen;
 *   3. MERGE    into what the subject already answered — never a replacement.
 *
 * Stage 3 is the one that makes the invariant the declaration already claims ("we filled this in
 * for you; change anything that is wrong") true for groups as well as scalars. Before it, a
 * non-empty array REPLACED the whole group, so a second lookup run threw away every row the
 * subject had added and every cell they had typed. Now: blank starter rows go, a candidate that
 * matches an existing row fills only that row's BLANK cells, unmatched candidates append after
 * the subject's own, and unmatched subject rows survive untouched.
 *
 * `undefined` means "nothing to contribute" — no source resolved to an array, or every item was
 * filtered out, or every candidate mapped to nothing. The caller writes no key at all, which is
 * how an empty register leaves the subject's rows exactly as they were.
 *
 * Bounded throughout by the group's OWN declaration: never more rows than `max`, never fewer than
 * `min` (topped up with blank rows so the subject still sees what they must fill in) — so autofill
 * can never produce a shape the subject could not have typed.
 *
 * It returns the PROVENANCE with the rows, because stage 3 is the only place that knows it: the
 * indices of the rows the register appended, which is precisely the set the "From the register"
 * chip may mark. A row the subject typed is never in it — not even the one a candidate matched and
 * topped up, because the row is still theirs.
 */
function groupRows(
  spec: GroupFieldSpec,
  group: LookupPopulateGroup,
  payload: unknown,
  current: FieldValue | undefined,
): MergedRows | undefined {
  const mergeKey = mergeKeyField(spec, group);
  const candidates = collapseCandidates(spec, mergeKey, buildCandidates(spec, group, payload));
  if (candidates.length === 0) return undefined;
  return bounded(spec, mergeIntoCurrent(spec, mergeKey, current, candidates));
}

/** The rows after stage 3, and which of them the upstream put there. */
interface MergedRows {
  readonly rows: GroupRow[];
  readonly contributed: number[];
}

/**
 * The declared sources, single-source shape included. `{target, from, map}` is read as the
 * one-source list it has always been, so there is ONE row-building path rather than a legacy
 * branch that drifts from the one under test.
 */
function populateSources(group: LookupPopulateGroup): readonly LookupPopulateGroupSource[] {
  if (Array.isArray(group.sources)) return group.sources;
  if (typeof group.from === "string") return [{ from: group.from, map: group.map ?? {} }];
  return [];
}

/** The `merge_key` subfield, but only when the target group really declares it. */
function mergeKeyField(spec: GroupFieldSpec, group: LookupPopulateGroup): string | undefined {
  const declared = group.merge_key;
  if (typeof declared !== "string" || declared.length === 0) return undefined;
  return (spec.fields ?? []).some((sub) => sub.name === declared) ? declared : undefined;
}

/** Stage 1 — one candidate row per surviving item, source by source, in declaration order. */
function buildCandidates(spec: GroupFieldSpec, group: LookupPopulateGroup, payload: unknown): GroupRow[] {
  const candidates: GroupRow[] = [];

  for (const source of populateSources(group)) {
    const items = readPath(payload, source.from);
    if (!Array.isArray(items)) continue;

    for (const item of items) {
      // The filter runs on the ITEM, before any mapping: `where` decides whether this row is ours
      // at all. The scope carries no fields and no ctx — a row filter has no form in scope, which
      // is exactly why its leaf paths are `item.`-rooted.
      // `party: {}` — a row filter has no form token's party row in scope either; the member is
      // REQUIRED on PredicateScope by design (Phase 515 task 1.4), never defaulted.
      if (!isVisible(source.where, { ctx: {}, fields: {}, party: {}, item })) continue;

      // Starts from the blank row, so every declared subfield is present in declared order and
      // an unmapped one submits as "" rather than as a missing key.
      const row = blankRow(spec);
      for (const [subfield, declared] of Object.entries(source.map ?? {})) {
        if (!Object.prototype.hasOwnProperty.call(row, subfield)) continue;
        if (typeof declared !== "string") continue;
        const sub = (spec.fields ?? []).find((candidate) => candidate.name === subfield);
        const cell = toRowCell(itemValue(item, declared));
        // Card #2908 — a register title the row's Title select cannot show ("The Hon") is left
        // blank so the required field asks, never written as a hidden value ({@link selectPrefill}).
        row[subfield] = sub === undefined ? cell : selectPrefill(sub, cell);
      }
      // A row that mapped to nothing at all is not a row — writing it would push a blank line in
      // front of the subject and, worse, could "match" one of their own rows at stage 3.
      if (!isBlankRow(spec, row)) candidates.push(row);
    }
  }

  return candidates;
}

/**
 * Stage 2 — candidates sharing the normalised `merge_key` cell become ONE row.
 *
 * The normaliser is deliberately DUMB (see the phase design): casefold, drop `,` and `.`, collapse
 * whitespace. It carries no honorific or corporate word list, because that problem is solved ONCE
 * upstream — the connector's `display_name` is what the two registers are compared through. A
 * second word list here would be exactly the mirror the server-side key says it does not have.
 */
function collapseCandidates(spec: GroupFieldSpec, mergeKey: string | undefined, candidates: GroupRow[]): GroupRow[] {
  if (mergeKey === undefined) return candidates;

  const merged: GroupRow[] = [];
  const byKey = new Map<string, GroupRow>();

  for (const candidate of candidates) {
    const key = mergeKeyOf(candidate[mergeKey]);
    const seen = key.length > 0 ? byKey.get(key) : undefined;
    if (seen === undefined) {
      merged.push(candidate);
      if (key.length > 0) byKey.set(key, candidate);
      continue;
    }
    unionInto(spec, seen, candidate);
  }

  return merged;
}

/**
 * Fold a later candidate into the one already standing for that human. A `checkbox_group` subfield
 * UNIONS its values in FIRST-SEEN order — which is why source order is load-bearing: the first
 * source's role leads the cell, and the downstream role precedence reads exactly that. Every other
 * subfield keeps the first NON-BLANK value, so a later source fills a gap but never rewrites an
 * answer the earlier one supplied.
 */
function unionInto(spec: GroupFieldSpec, into: GroupRow, from: GroupRow): void {
  for (const sub of spec.fields ?? []) {
    if (sub.type === "checkbox_group") {
      // Read BACK through the same cell projection the controls use, union, and write through the
      // same one — so the separator is never spelled here.
      const union = asStringArray(fromRowCell(sub.type, into[sub.name]));
      for (const value of asStringArray(fromRowCell(sub.type, from[sub.name]))) {
        if (!union.includes(value)) union.push(value);
      }
      into[sub.name] = toRowCell(union);
      continue;
    }
    if (isBlankCell(into[sub.name])) into[sub.name] = from[sub.name] ?? "";
  }
}

/**
 * Stage 3 — the never-wipe merge. The subject's rows keep their order and their content; a
 * candidate that matches one fills only its BLANK cells (notably: never an email they typed);
 * candidates that match nothing append after the subject's own rows.
 *
 * The appended ones are the CONTRIBUTED rows, recorded as they are pushed: provenance falls out of
 * the merge rather than being re-derived from the result, so there is no second reading of "which
 * rows are new" to drift from this one.
 */
function mergeIntoCurrent(
  spec: GroupFieldSpec,
  mergeKey: string | undefined,
  current: FieldValue | undefined,
  candidates: readonly GroupRow[],
): MergedRows {
  const rows = asRows(current)
    .map((row) => declaredCells(spec, row))
    .filter((row) => !isBlankRow(spec, row));
  const taken = new Set<number>();

  for (const row of rows) {
    const match = candidates.findIndex((candidate, at) => !taken.has(at) && sameRow(spec, mergeKey, row, candidate));
    const matched = candidates[match];
    if (matched === undefined) continue;
    taken.add(match);
    fillBlanks(spec, row, matched);
  }

  const contributed: number[] = [];
  candidates.forEach((candidate, at) => {
    if (taken.has(at)) return;
    contributed.push(rows.length);
    rows.push(candidate);
  });

  return { rows, contributed };
}

/**
 * Is this candidate the row the subject already has? On the normalised `merge_key` cell when the
 * declaration names one — that IS the identity it declared. With no `merge_key`, on AGREEMENT:
 * every cell the candidate fills already reads the same in that row. So re-running a lookup over
 * an unchanged register writes nothing twice, while a candidate the subject has never seen can
 * never claim one of their rows and fill its gaps with a stranger's details.
 */
function sameRow(spec: GroupFieldSpec, mergeKey: string | undefined, row: GroupRow, candidate: GroupRow): boolean {
  if (mergeKey !== undefined) {
    const key = mergeKeyOf(candidate[mergeKey]);
    return key.length > 0 && mergeKeyOf(row[mergeKey]) === key;
  }
  return (spec.fields ?? []).every((sub) => {
    const cell = candidate[sub.name];
    return isBlankCell(cell) || mergeKeyOf(row[sub.name]) === mergeKeyOf(cell);
  });
}

/** Fills the gaps in a row the subject owns — and only the gaps. */
function fillBlanks(spec: GroupFieldSpec, row: GroupRow, candidate: GroupRow): void {
  for (const sub of spec.fields ?? []) {
    if (!isBlankCell(row[sub.name])) continue;
    row[sub.name] = candidate[sub.name] ?? "";
  }
}

/**
 * The declared subfields of a row the subject owns, each projected back to the cell its own
 * TYPE says a row holds — so a re-projection keeps an `address` subfield's nested value instead
 * of eating it, exactly as a draft restore does ({@link toRowCellFor}).
 */
function declaredCells(spec: GroupFieldSpec, row: GroupRow): GroupRow {
  const cells = blankRow(spec);
  const source = row as Record<string, unknown>;
  for (const sub of spec.fields ?? []) {
    cells[sub.name] = toRowCellFor(sub.type, source[sub.name] as FieldValue | undefined);
  }
  return cells;
}

/** A row with nothing in any declared cell — a starter row, or a mapping that resolved to nothing. */
function isBlankRow(spec: GroupFieldSpec, row: GroupRow): boolean {
  return (spec.fields ?? []).every((sub) => isBlankCell(row[sub.name]));
}

/**
 * The DUMB identity of a `merge_key` cell — see {@link collapseCandidates}. `unknown` in,
 * because a row cell is no longer necessarily a string and the non-string guard below is
 * already the whole answer for one: a cell with no string reading has no identity, so it
 * matches nothing, which is the never-wipe posture {@link sameRow} is built on.
 */
function mergeKeyOf(cell: unknown): string {
  if (typeof cell !== "string") return "";
  return cell.toLowerCase().replace(/[,.]/g, "").replace(/\s+/g, " ").trim();
}

/**
 * The group's own `min` / `max`, applied to the MERGED rows — and to their provenance with them: a
 * row the cap dropped was never shown, and a blank row the `min` top-up added came from nobody.
 */
function bounded(spec: GroupFieldSpec, merged: MergedRows): MergedRows {
  const max = typeof spec.max === "number" ? Math.max(0, spec.max) : undefined;
  // Capping from the END drops appended candidates first: the cap can never cost the subject a
  // row they typed.
  const capped = max === undefined ? merged.rows : merged.rows.slice(0, max);
  const contributed = merged.contributed.filter((at) => at < capped.length);
  const min = Math.max(0, spec.min ?? 0);
  while (capped.length < min) capped.push(blankRow(spec));
  return { rows: capped, contributed };
}

// ── Phase 524 — the PICK: many candidates, one choice ──────────────────────────────────────
//
// Some upstreams answer a question with a SHORTLIST rather than a record: the subject knows which
// one is theirs and nothing else does. `choose` is that shape, and it changes nothing about how a
// value reaches a field — the pick runs the same scalar mapping an autofill runs, against one item
// of the array instead of against the whole envelope. Two things follow, and both are properties
// rather than conventions: there is no second mapping grammar to learn, and a pick is as
// overwritable as anything else the subject typed.

/** One labelled line under a candidate's headline, already projected to display text. */
export interface LookupCandidateRow {
  readonly label: string;
  readonly value: string;
}

/** One candidate as the list renders it — plus the envelope item a pick maps back onto fields. */
export interface LookupCandidate {
  readonly title: string;
  readonly subtitle?: string;
  readonly rows: readonly LookupCandidateRow[];
  /**
   * The envelope ITEM this candidate was projected from. It is carried rather than re-read so the
   * thing the subject picked is the thing that gets mapped — an index into the array would go
   * stale the moment the list is re-sorted or re-fetched.
   */
  readonly raw: unknown;
}

/**
 * The candidates a `choose` lookup offers, projected from the RESULT (the envelope sits under
 * `response`, exactly as {@link lookupPatch} reads it).
 *
 * Anything that does not resolve is simply absent: a row with no value is dropped rather than
 * shown blank, and an array that is not there — or not an array — is ZERO candidates, which the
 * renderer says out loud. This function never decides on its own which matches are worth seeing;
 * the only item it drops is one the DECLARATION's `choose.where` rejects (card #2717 — e.g. a
 * staff member the upstream still lists after they left), evaluated by the SAME evaluator and
 * scope a `populate_group` source's `where` runs on. ABSENT ⇒ every item is offered.
 */
export function chooseCandidates(lookup: FormLookup, payload: unknown): LookupCandidate[] {
  const choose = lookup.choose;
  if (choose === undefined) return [];

  const items = readPath(payload, choose.from);
  if (!Array.isArray(items)) return [];

  // The filter runs on the ITEM, before any projection — the populate_group row filter's scope
  // exactly: no fields, no ctx, no party, just the candidate (its leaves are `item.`-rooted).
  const offered = items.filter((item) => isVisible(choose.where, { ctx: {}, fields: {}, party: {}, item }));

  return offered.map((item) => {
    const subtitle = envelopeDisplayValue(itemValue(item, choose.subtitle)).trim();
    return {
      title: envelopeDisplayValue(itemValue(item, choose.title)).trim(),
      subtitle: subtitle.length > 0 ? subtitle : undefined,
      rows: (choose.rows ?? [])
        .map(({ label, path }) => ({ label, value: envelopeDisplayValue(itemValue(item, path)).trim() }))
        .filter((row) => row.value.length > 0),
      raw: item,
    };
  });
}

/**
 * The answers a PICK writes, as a patch over the current values — the `choose.populate` half of
 * the declaration, read against the picked item.
 *
 * It is {@link lookupPatch}'s scalar half with a different root, deliberately: same declared-field
 * whitelist, same per-type projection, same "a path that resolves to nothing writes nothing". A
 * pick therefore ADDS answers and never wipes one, and every field it fills stays an ordinary
 * answer the subject can correct.
 */
export function choosePatch(schema: FormSchema, lookup: FormLookup, item: unknown): FormValues {
  const choose = lookup.choose;
  if (choose === undefined) return {};
  return scalarPatch(fieldIndex(schema), choose.populate, (declared) => itemValue(item, declared));
}

